import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RequirementCardContainer } from 'components/Shared/Fields/styles'
import CampaignEditorInputFieldContainer from 'components/Shared/Permission/CampaignEditorInputFieldContainer'
import { fieldIdentifiers } from 'components/Shared/Comment'
import FieldTitleAndSubTitle from 'components/Shared/Fields/FieldTitleAndSubTitle'
import { useSelector } from 'react-redux'
import { selectProfileTypeOptions } from 'state/constants'
import Dropdown from 'particles/Dropdown'
import { useField, useFormikContext } from 'formik'
import MenuItem from 'particles/MenuItem'
import { EmptySearchResult } from 'particles/MenuItem/Search'
import { ProfileTypeOption } from 'services/api/constants'

interface ProfileTypeRequirementProps {
    name: string
    onRemove: () => void
    isReadOnly?: boolean
    requirementUuid?: string
}

const MAX_PROFILE_TYPES = 5

const ProfileTypeRequirement: React.FC<ProfileTypeRequirementProps> = ({
    name,
    onRemove,
    isReadOnly,
    requirementUuid,
}) => {
    const [t] = useTranslation()
    const { setFieldValue } = useFormikContext()
    const [, meta] = useField<number[] | null>(name)
    const isError = meta.touched && Boolean(meta.error)
    const profileTypeOptions = useSelector(selectProfileTypeOptions)
    const [profileTypes, setProfileTypes] = useState<ProfileTypeOption[] | null>(profileTypeOptions)

    useEffect(() => {
        handleClearSearch()
    }, [profileTypeOptions])

    const handleSearch = (value: string) => {
        const newProfileTypes = profileTypeOptions ? [...profileTypeOptions] : []
        setProfileTypes(newProfileTypes.filter(item => item.label.toLowerCase().includes(value.toLowerCase())))
    }
    const handleClearSearch = () => {
        setProfileTypes(profileTypeOptions)
    }

    const isAvailableOptionsDisabled = (id: number) =>
        !meta.value?.includes(id) && meta.value?.length === MAX_PROFILE_TYPES

    return (
        <RequirementCardContainer isCard>
            <FieldTitleAndSubTitle
                title={t('campaignEditor.requirement.profileTypeSectionMenuHeader')}
                subtitle={t('campaignEditor.requirement.profileTypeSubtitle')}
                isRequired
                isReadOnly={isReadOnly}
                onClose={onRemove}
            />
            <CampaignEditorInputFieldContainer>
                <Dropdown
                    label={t('campaignEditor.requirement.typeHeader')}
                    placeholder={t('placeholderText.dropdown.requestEditsReason')}
                    onChange={(value): void => {
                        setFieldValue(name, value)
                    }}
                    errorMessage={isError ? meta.error : undefined}
                    value={meta.value}
                    isRequired
                    identifier={`${requirementUuid}_${fieldIdentifiers.profileTypes}`}
                    isReadOnly={isReadOnly}
                    multiple
                    chipProps={{ maxWidth: 300 }}
                >
                    <MenuItem.Search
                        placeholder={t('placeholderText.genericSearch')}
                        onSearch={handleSearch}
                        onClearSearch={handleClearSearch}
                    />
                    {profileTypes && profileTypes.length > 0 ? (
                        profileTypes.map((option, index) => (
                            <MenuItem.Checkbox
                                key={`search-dropdown-${index}`}
                                text={option.label}
                                value={option.id}
                                tabIndex={index}
                                isDisabled={isAvailableOptionsDisabled(option.id)}
                            />
                        ))
                    ) : (
                        <EmptySearchResult />
                    )}
                </Dropdown>
            </CampaignEditorInputFieldContainer>
        </RequirementCardContainer>
    )
}

export default ProfileTypeRequirement
